const { putRecordBatch } = require("../lib/firehose");
const { validateMessages } = require("../lib/validation");
const config = require("./config");
const cookie = require("cookie");
const {
  initialiseIDs,
  isUuid,
  cookieSettings,
  parseQueryFromDocument,
  isInvalidID
} = require("../lib/util");
const CredentialsManager = require('./CredentialsManager')

const INITIAL_RETRY_TIMEOUT = 100
const MAX_RETRY_TIMEOUT = 1000
const CREDENTIALS_MANAGER = '__BASIN_SLS_CREDENTIALS_MANAGER__'

function getUuids() { // client-side
  if (typeof document === "undefined") {
    console.warn('getUuids() is a client-side function')
    return { sessionId: null, dailyId: null, permanentId: null };
  }

  const browserCookies = cookie.parse(
    typeof document.cookie === "string" ? document.cookie : ""
  );

  const {
    BasinSessionId: sessionId,
    BasinDailyId: dailyId,
    BasinPermanentId: permanentId
  } = browserCookies;

  return {
    sessionId: isUuid(sessionId) ? sessionId : null,
    dailyId: isUuid(dailyId) ? dailyId : null,
    permanentId: isUuid(permanentId) ? permanentId : null
  };
}

function setCookies(uuids, enableSecureCookie) {
  if (typeof document === "undefined" || typeof document.cookie !== 'string') {
    console.warn('setCookies() is a client-side function')
    return;
  }

  const browserCookies = cookie.parse(document.cookie)

  if(!isUuid(browserCookies.BasinSessionId)) {
    document.cookie = cookie.serialize(
      'BasinSessionId',
      uuids.sessionId,
      {
        ...cookieSettings.sessionId,
        secure: enableSecureCookie
      }
    )
  }

  if(!isUuid(browserCookies.BasinDailyId)) {
    document.cookie = cookie.serialize(
      'BasinDailyId',
      uuids.dailyId,
      {
        ...cookieSettings.dailyId,
        maxAge: cookieSettings.dailyId.maxAge / 1000,
        secure: enableSecureCookie
      }
    )
  }

  if(!isUuid(browserCookies.BasinPermanentId)) {
    document.cookie = cookie.serialize(
      'BasinPermanentId',
      uuids.permanentId,
      {
        ...cookieSettings.permanentId,
        maxAge: cookieSettings.permanentId.maxAge / 1000,
        secure: enableSecureCookie
      }
    )
  }
}

class Basin {
  constructor(opts = {}) {
    var { channel, source, environment, debug } = opts;

    // validate basin instance configuration
    try {
      if (!channel) {
        throw new Error("Channel must be defined. See README for details.");
      }

      if (!source) {
        throw new Error("Source must be defined. See README for details.");
      }

      if (!environment) {
        throw new Error("Environment option not defined.");
      }

      if(environment == 'prod') environment = 'production';
      if(environment == 'staging') environment = 'stage';

      this.config = config.getConfig(environment);

      if (!this.config) {
        throw new Error("Config for defined environment does not exist");
      }
    } catch (e) {
      debug && console.error("Basin failed to start:", e.message);

      return null;
    }

    const query = (typeof document !== 'undefined')
      ? parseQueryFromDocument(document)
      : {}

    // set up instance properties
    const uuids = getUuids();

    // invalidate daily id if invalid
    const resetDailyId = isInvalidID(uuids.dailyId);

    if (resetDailyId) {
      uuids.dailyId = null;
      document.cookie = cookie.serialize(
        'BasinDailyId',
        '',
        {
          ...cookieSettings.dailyId,
          expires: new Date(0),
        }
      );
    }

    const ids = initialiseIDs(uuids);

    this.uuids = ids;

    setCookies(ids, this.config.EnableSecureCookie);

    this.queue = [];
    this.debug = debug || query.debug;
    this.channel = channel;
    this.source = source;
    this.credentialsManager = this.getCredentialsManager();

    if (typeof opts.metadata === "object") {
      this.metadata = opts.metadata;
    }

    this.credentialsManager
        .getCredentialsAsync(this.config.CognitoIdentityId)
        .then(credentials => {
          this.credentials = credentials;
          if (this.queue.length > 0) {
            this.flushQueue(); // send any messages queued up while fetching credentials
          }
        })
        .catch(error => {
          debug && console.error("Couldn't get firehose credentials", error);
        })
  }

  getCredentialsManager() {
    if (typeof window === 'undefined') {
      return new CredentialsManager();
    }
    if (window[CREDENTIALS_MANAGER] == null) {
      window[CREDENTIALS_MANAGER] = new CredentialsManager()
    }
    if (window[CREDENTIALS_MANAGER] instanceof CredentialsManager) {
      return window[CREDENTIALS_MANAGER]
    }
    return new CredentialsManager()
  }

	updateMetadata(newMetadata = {}) {
    this.metadata = {
      ...this.metadata,
      ...newMetadata
    }
	}

  scheduleRetry(timeout) {
    let nextTimeout = timeout ? Math.min(MAX_RETRY_TIMEOUT, timeout * 2) : INITIAL_RETRY_TIMEOUT
    setTimeout(() => this.flushQueue(nextTimeout), nextTimeout)
  }

  parseData(recordToParse) {
    try {
      return JSON.parse(recordToParse.Data)
    } catch (err) {
      return JSON.parse(`"${recordToParse.Data}"`)
    }
  }

  async flushQueue(timeout) {
    if (this.credentials === undefined) {
      return Promise.reject("Credentials are missing");
    }

    const expirationDateInMilliseconds = this.credentials.Expiration * 1000
    const oneMinuteInMs = 60000;

    // check if cognito identity credentials have expired or not
    // if they have get new credentials
    // if not request fails and gets caught in the catch
    if (Date.now() - oneMinuteInMs > expirationDateInMilliseconds) {
      this.credentials = await this.credentialsManager.getCredentialsAsync(this.config.CognitoIdentityId, true);
    }

    // copy and empty queue
    const records = this.queue.slice();
    this.queue = [];

    // console output, more verbose in debugging mode
    records.forEach(record => {
      const parsedRecord = this.parseData(record)
      const dataToLog = this.debug ? parsedRecord : parsedRecord.name || parsedRecord
      console.info('Basin', dataToLog)
    });

    // validate messages against data guild manifests
    this.debug && await validateMessages(records, this.config.ValidationUrl)

    // send messages
    if(records.length > 0){
      await putRecordBatch(this.config.DeliveryStreamName, records, this.credentials)
          .then(resp => {
            if (resp.FailedPutCount > 0) {
              // some messages failed, put those back
              const failedRecords = records.filter(
                  (_, index) => resp.RequestResponses[index].ErrorCode
              );

              this.queue = this.queue.concat(failedRecords);

              this.scheduleRetry(timeout)
            }
            return resp;
          })
          .catch(err => {
            // whole request failed, put everything back!
            this.queue = this.queue.concat(records);
            console.log(err);
            this.scheduleRetry(timeout)
          });
    }
  }

  async send(eventName, eventData) {
    // queue a message and send
    const name = `${this.channel}.${this.source}.${eventName}`;
    const payload = {
      ...this.uuids,
      ...this.metadata,
      ...eventData,
      created: new Date().toISOString()
    };

    this.queue.push({
      Data: JSON.stringify({ name, payload })
    });
    
    try {
      const result = await this.flushQueue();
      return result;
    }
    catch(err)
    {
      this.debug && console.log(err);
    }
  }
}

exports.Basin = Basin;
if(typeof window !== 'undefined') {
	window.tm_basin = Basin;
	window.TM_Basin = Basin;
}
